/**
 * Practical — logistics, once and never again (REDESIGN.md §7).
 *
 * Hairline rows that open on tap. The content is not a separate feature: it is
 * the published `info` brief, split on its own headings, so whatever the admin
 * writes there appears here without a second place to maintain.
 *
 * Available offline like any other screen — the brief is in the data cache the
 * service worker keeps.
 */

import { apiGet } from '../js/api.js'
import { emptyState } from '../components/empty.js'
import { icon } from '../js/icons.js'
import { text } from '../js/html.js'

export function renderPractical({ timeline = [], retreat }) {
  const briefs = practicalBriefs(timeline, retreat)

  return `
    <p class="eyebrow">Logistics, once and never again</p>
    <h1 class="h1">Practical info</h1>
    <p class="sub">Everything you need. Available offline.</p>

    <div data-practical>
      ${
        briefs.length === 0
          ? emptyState({
              icon: 'info',
              title: 'Arriving in time',
              body: 'The practical brief opens two weeks before departure.',
            })
          : '<p class="lede">Loading…</p>'
      }
    </div>
  `
}

/**
 * Which briefs are "practical".
 *
 * Kind alone is not enough: the daily schedules on the island are `info` too.
 * What separates them is when they arrive — logistics is written before you
 * leave, a schedule during. So: published `info` briefs dated before the
 * retreat starts, in order.
 *
 * A column saying "this one is the practical brief" would be the firmer
 * answer, and belongs with the admin work rather than here.
 */
function practicalBriefs(timeline, retreat) {
  const info = timeline.filter((entry) => !entry.locked && entry.kind === 'info')

  if (retreat?.starts_on === undefined || retreat?.starts_on === null) {
    return info.slice(0, 1)
  }

  const before = info.filter((entry) => entry.publish_at.slice(0, 10) < retreat.starts_on)

  return before.length > 0 ? before : info.slice(0, 1)
}

/**
 * Fetches the brief and lays its headings out as rows. Done after render
 * because the timeline carries only an excerpt, and the sections live in the
 * body.
 */
export async function mountPractical(root, { timeline = [], retreat }) {
  const slot = root.querySelector('[data-practical]')
  const briefs = practicalBriefs(timeline, retreat)

  if (slot === null || briefs.length === 0) {
    return
  }

  // Offline, the bodies may not be in the cache — but the timeline is, and it
  // carries an excerpt of each. A short row beats an apology.
  const bodies = await Promise.all(
    briefs.map(async (brief) => {
      try {
        return { title: brief.title, html: (await apiGet(`/briefs/${brief.id}`)).brief.body_html }
      } catch {
        return {
          title: brief.title,
          html: brief.excerpt ? `<p>${text(brief.excerpt)}</p>` : '',
          partial: true,
        }
      }
    }),
  )

  // A brief with headings contributes one row per heading; one without becomes
  // a single row under its own title.
  const sections = bodies.flatMap((brief) => {
    const found = brief.partial ? [] : split(brief.html)

    return found.length > 0 ? found : [{ title: brief.title, body: [brief.html] }]
  })

  slot.innerHTML = sections.map(row).join('')

  if (bodies.some((brief) => brief.partial)) {
    slot.insertAdjacentHTML(
      'beforeend',
      '<p class="hint">Some of this is the short version — the full text returns with the connection.</p>',
    )
  }

  slot.querySelectorAll('.info__head').forEach((head) => {
    head.addEventListener('click', () => {
      const open = head.parentElement.classList.toggle('info--open')
      head.setAttribute('aria-expanded', String(open))
    })
  })
}

/**
 * Splits rendered markdown on its headings. The HTML comes from our own
 * renderer, so it is parsed rather than pattern-matched — and parsing it into a
 * detached template never runs anything, scripts included.
 */
function split(html) {
  const holder = document.createElement('template')
  holder.innerHTML = html

  const sections = []
  let current = null

  for (const node of holder.content.childNodes) {
    if (node.nodeName === 'H2' || node.nodeName === 'H3') {
      current = { title: node.textContent.trim(), body: [] }
      sections.push(current)

      continue
    }

    if (current !== null && node.nodeType === Node.ELEMENT_NODE) {
      current.body.push(node.outerHTML)
    }
  }

  return sections.filter((section) => section.title !== '')
}

function row(section) {
  return `
    <div class="info">
      <button class="info__head" aria-expanded="false">
        <span>${text(section.title)}</span>
        ${icon('chevronRight', { size: 16 })}
      </button>
      <div class="info__body prose">${section.body.join('')}</div>
    </div>
  `
}
